import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Clock, Calendar, ArrowUpRight, ChevronRight, Home as HomeIcon } from "lucide-react";
import { BLOG_POSTS, BLOG_ORDER } from "@/data/blog-posts";

const BASE_URL = "https://bestspinesurgeonhyderabad.com";

export default function Blog() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    document.title = "Spine & Brain Health Blog | Dr. Arun Reddy M — Hyderabad";
    const desc = document.querySelector('meta[name="description"]');
    const prevDesc = desc ? desc.getAttribute("content") : null;
    if (desc) desc.setAttribute("content", "Patient education articles on spine and brain health by neurosurgeon Dr. Arun Reddy M — endoscopic surgery, sciatica, MRI reports and recovery.");
    const canonical = document.querySelector('link[rel="canonical"]');
    const prevCanonical = canonical ? canonical.getAttribute("href") : null;
    if (canonical) canonical.setAttribute("href", `${BASE_URL}/blog`);
    return () => {
      document.title = prevTitle;
      if (desc && prevDesc !== null) desc.setAttribute("content", prevDesc);
      if (canonical && prevCanonical !== null) canonical.setAttribute("href", prevCanonical);
    };
  }, []);

  return (
    <div data-testid="blog-index" className="bg-white">
      <section className="relative pt-32 pb-14 md:pt-40 md:pb-16 border-b border-slate-100">
        <div className="absolute inset-0 bg-[radial-gradient(120%_80%_at_85%_-10%,#e8f1fb_0%,#f4f8fd_45%,#ffffff_80%)]" />
        <div className="relative max-w-6xl mx-auto px-6 md:px-10">
          <nav aria-label="Breadcrumb" className="mb-6 flex items-center gap-1 text-sm text-slate-500" data-testid="blog-breadcrumbs">
            <Link to="/" className="flex items-center gap-1 hover:text-brand transition-colors">
              <HomeIcon className="w-3.5 h-3.5" /> Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5" />
            <span className="text-[#0b2c4d] font-medium">Blog</span>
          </nav>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand">Patient Education</p>
          <h1 className="mt-3 font-heading font-extrabold text-[#0b2c4d] text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.02]">
            Insights from the clinic
          </h1>
          <p className="mt-5 text-lg text-slate-600 max-w-2xl">
            Plain-language articles on spine and brain health, written to help you make informed
            decisions — not to alarm you.
          </p>
        </div>
      </section>

      <section className="py-16 md:py-20">
        <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-2 gap-8">
          {BLOG_ORDER.map((slug) => {
            const p = BLOG_POSTS[slug];
            return (
              <Link
                key={slug}
                to={`/blog/${slug}`}
                data-testid={`blog-card-${slug}`}
                className="group glass-card rounded-2xl overflow-hidden hover:-translate-y-1.5 hover:shadow-lg transition-all duration-300 flex flex-col"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={p.image}
                    alt={p.title}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#0b2c4d]/70 to-transparent" />
                  <span className="absolute top-3 left-3 text-[10px] uppercase tracking-wider text-brand font-bold bg-white px-2.5 py-1 rounded-full">
                    {p.category}
                  </span>
                </div>
                <div className="p-6 flex flex-col flex-1">
                  <h2 className="font-heading font-extrabold text-[#0b2c4d] text-xl tracking-tight leading-snug">
                    {p.title}
                  </h2>
                  <p className="mt-3 text-sm text-slate-500 leading-relaxed flex-1">{p.intro.slice(0, 150)}…</p>
                  <div className="mt-4 flex items-center gap-4 text-slate-400 text-xs">
                    <span className="flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.minutes}</span>
                    <span className="flex items-center gap-1"><Calendar className="w-3.5 h-3.5" /> {p.date}</span>
                  </div>
                  <span className="mt-4 inline-flex items-center gap-1 text-sm text-brand font-semibold">
                    Read Article
                    <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </span>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
